import React, { useState, memo, useEffect, useCallback, useRef } from "react";
import { ServerDataLoader, IColumnSort, IColumnFilters } from "./models";
import { isFirstPageOfApiResponse } from "../../../models/PagedApiResponse";
import DataTable, { IDataTableProps } from "./index";
import { showToast } from "../../../services/ToasterService";


type CommonProps<TRow> = Pick<IDataTableProps<TRow>, "pageSize" | "columns" | "getRowKey" | "containerStyle">;

interface IProps<TRow> extends CommonProps<TRow> {
  dataLoader: ServerDataLoader<TRow>;
  defaultSort?: IColumnSort<TRow>;
  filters?: IColumnFilters<TRow>;
  onDataLoaded?: (data: TRow[]) => void;
  refreshToken?: number;
}


const DataTableContainer = <TRow extends {}>({ pageSize, dataLoader, columns, defaultSort, filters, getRowKey, containerStyle, onDataLoaded, refreshToken }: IProps<TRow>) => {
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [sorting, setSorting] = useState(defaultSort);

  const pageNumRef = useRef(1);
  const [promiseResult, setPromiseResult] = useState<{result?: TRow[], error?: Error | unknown}>({});

  const loadData = useCallback(async (pageNum: number, isPageNav = true) => {
    pageNumRef.current = pageNum;
    setPageNum(current => {
      if (pageNum === 1 && current !== pageNum && !isPageNav) {
        showToast("Page reset to page 1");
      }
      return pageNum;
    });

    setPromiseResult({});
    try {
      const result = await dataLoader(pageNum, pageSize, sorting, filters);
      isFirstPageOfApiResponse(result) && setTotalRecords(result.totalRecords!);
      setPromiseResult({result: result.data});
      onDataLoaded?.(result.data);
    }
    catch(error) {
      setPromiseResult({error});
      setTotalRecords(0); // Used to hide footer
    }
  }, [dataLoader, pageSize, sorting, filters, onDataLoaded]);


  useEffect(() => {
    loadData(1, false);
  }, [loadData]);

  const loadDataRef = useRef(loadData);
  useEffect(() => { loadDataRef.current = loadData; }, [loadData]);

  useEffect(() => {
    if (!refreshToken) return;
    loadDataRef.current(pageNumRef.current, true);
  }, [refreshToken]);


  return (
    <DataTable<TRow>
      pageSize={pageSize}
      pageNum={pageNum}
      setPageNum={loadData}
      totalRecords={totalRecords}
      sorting={sorting}
      setSorting={setSorting}
      data={promiseResult.result}
      error={promiseResult.error}
      columns={columns}
      getRowKey={getRowKey}
      containerStyle={containerStyle}
    />
  );

}

const memoized = memo(DataTableContainer) as typeof DataTableContainer;
export default memoized;


